import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  CircularProgress,
} from '@mui/material';
import { UploadCloud } from 'lucide-react';
import {
  siteLanguagesService,
  getLanguageName,
} from 'components/api/siteLanguages';
import SupportedLanguageSelect from 'components/common/SupportedLanguageSelect/SupportedLanguageSelect';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import styles from './LangTab.module.scss';

interface AddLanguageModalProps {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddLanguageModal: React.FC<AddLanguageModalProps> = ({
  open,
  siteId,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedCode, setSelectedCode] = useState('');
  const [isCustomCode, setIsCustomCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [name, setName] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedCode('');
      setIsCustomCode(false);
      setCustomCode('');
      setName('');
      setIsEnabled(true);
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const finalCode = (isCustomCode ? customCode : selectedCode).trim().toLowerCase();

  const handleSubmit = async () => {
    if (!finalCode) {
      dispatch(showToast({ text: 'Language code is required', type: 'error' }));
      return;
    }

    try {
      setSubmitting(true);
      await siteLanguagesService.createSiteLanguage({
        siteId,
        code: finalCode,
        name: name.trim() || getLanguageName(finalCode),
        logo: logoFile,
        isEnabled,
      });

      dispatch(showToast({ text: 'Site language added successfully', type: 'success' }));
      onSuccess();
      onClose();
    } catch (error: unknown) {
      console.error('Failed to add site language:', error);
      let msg = 'Failed to add site language';
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.message || error.message || msg;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      classes={{ paper: styles.dialogPaper }}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle className={styles.dialogTitle}>Add Site Language</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <div className={styles.formGroup}>
          <SupportedLanguageSelect
            value={isCustomCode ? '__custom__' : selectedCode}
            onChange={(val, selectedLang) => {
              if (val === '__custom__') {
                setIsCustomCode(true);
                setSelectedCode('');
                setName('');
              } else {
                setIsCustomCode(false);
                setSelectedCode(val);
                setName(selectedLang?.name || getLanguageName(val));
              }
            }}
            disabled={submitting}
          />
        </div>

        {isCustomCode && (
          <div className={styles.formGroup}>
            <label>Custom Language Code (e.g., en, es, pt)</label>
            <input
              type="text"
              placeholder="e.g. en"
              className={styles.formInput}
              value={customCode}
              onChange={(e) => {
                const code = e.target.value;
                setCustomCode(code);
                if (!name || name === getLanguageName(customCode)) {
                  setName(getLanguageName(code));
                }
              }}
              disabled={submitting}
              autoFocus
            />
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Language Display Name</label>
          <input
            type="text"
            placeholder="e.g. English"
            className={styles.formInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className={styles.toggleRow}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleTitle}>Enable Language</span>
            <span className={styles.toggleHint}>Active and visible on site localization</span>
          </div>
          <Switch
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            disabled={submitting}
            color="primary"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Language Logo (Optional)</label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className={styles.hiddenFileInput}
          />
          <div
            className={styles.logoUploadBox}
            onClick={() => fileInputRef.current?.click()}
          >
            {logoPreview ? (
              <div className={styles.previewContainer}>
                <img src={logoPreview} alt="Logo preview" className={styles.previewImg} />
                <button
                  type="button"
                  className={styles.removeFileBtn}
                  onClick={handleRemoveFile}
                >
                  Remove image
                </button>
              </div>
            ) : (
              <>
                <UploadCloud className={styles.uploadIcon} size={28} />
                <span className={styles.uploadText}>Click to select logo file</span>
                <span className={styles.uploadHint}>SVG, PNG, WEBP or JPG</span>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !finalCode}
          className={styles.addBtn}
        >
          {submitting ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              Adding...
            </div>
          ) : (
            'Add Language'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLanguageModal;
